import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const NCB_INSTANCE = import.meta.env.VITE_NCB_INSTANCE;
  const NCB_API_URL = import.meta.env.VITE_NCB_API_URL;
  const NCB_SECRET_KEY = import.meta.env.VITE_NCB_SECRET_KEY;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      // Example admin check
      if (parsedUser.email === 'admin@example.com') {
        setIsAdmin(true);
      }
    }
    setLoading(false);
  }, []);

  const apiFetch = async (endpoint, options = {}) => {
    const url = `${NCB_API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'X-Database-Instance': NCB_INSTANCE,
      'Authorization': `Bearer ${NCB_SECRET_KEY}`,
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'An API error occurred');
    }

    return response.json();
  };

  const createUserProfile = async (name, email, userId) => {
    try {
      const endpoint = `/create/users?Instance=${NCB_INSTANCE}`;
      const profileData = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          username: name,
          email: email,
          user_id: String(userId),
        }),
      });
      console.log('User profile created:', profileData);
      return { username: name, email, user_id: String(userId) };
    } catch (error) {
      // It's possible the profile already exists, which is not a critical failure.
      console.error('Failed to create user profile:', error.message);
      // Return basic info anyway
      return { username: name, email, user_id: String(userId) };
    }
  };

  const fetchUserProfile = async (email, userId, name) => {
    try {
      const searchEndpoint = `/search/users?Instance=${NCB_INSTANCE}`;
      const searchResult = await apiFetch(searchEndpoint, {
        method: 'POST',
        body: JSON.stringify({ email: email }),
      });

      if (searchResult.data && searchResult.data.length > 0) {
        return searchResult.data[0];
      } else {
        // If no profile exists, create one for the user
        console.log("No profile found for existing user, creating one...");
        return await createUserProfile(name || email.split('@')[0], email, userId);
      }
    } catch (error) {
      console.error('Failed to fetch/create user profile:', error.message);
      return null;
    }
  };

  const signInWithEmail = async (email, password) => {
    const endpoint = '/sign-in/email';
    const response = await apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.user) {
      const userProfile = await fetchUserProfile(response.user.email, response.user.id, response.user.name);
      
      const fullUserData = {
        ...response.user,
        ...userProfile, // This will add username, created_at etc.
      };

      setUser(fullUserData);
      localStorage.setItem('user', JSON.stringify(fullUserData));
      if (fullUserData.email === 'admin@example.com') {
        setIsAdmin(true);
      }
      return { user: fullUserData };
    }
    throw new Error('Sign in failed');
  };

  const signUpWithEmail = async (name, email, password) => {
    const endpoint = '/sign-up/email';
    const response = await apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });

    if (response.user) {
      const userProfile = await createUserProfile(name, response.user.email, response.user.id);
      
      const fullUserData = {
        ...response.user,
        ...userProfile,
      };

      setUser(fullUserData);
      localStorage.setItem('user', JSON.stringify(fullUserData));
       if (fullUserData.email === 'admin@example.com') {
        setIsAdmin(true);
      }
      return { user: fullUserData };
    }
    throw new Error('Sign up failed');
  };

  const signOut = async () => {
    // NoCodeBackend sign-out is cookie-based, but we clear local state regardless.
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem('user');
    // We don't need to call the API here as per the original logic,
    // session clearing is handled client-side in this architecture.
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, signInWithEmail, signUpWithEmail, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);